import { computed, unref } from "vue";
import { useI18n } from "vue-i18n";

import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import { AgentFeatureType, type AgentVisibilityType } from "@/enums/ai";
import { getUpdatedVisibilities } from "@/helpers/agents";
import { useNotification } from "@/hooks/notifications";
import { queryClient } from "@/query/queryClient";
import { aiAgentsApiService } from "@/services/modules/aiAgents";
import { useAiAgentsStore } from "@/stores/ai/agents";

import type {
  AiAgentBase,
  AiAgentSession,
  UpdateAgentsVisibilityPayload,
} from "@/models/aiAgents";
import type { IPaginatedResponse } from "@/models/common";
import type { MaybeRef, Ref } from "vue";

export const QUERY_KEYS = {
  agents: (search: Ref<string>) => ["aiAgents", "infinite", search],
  agentSessions: (agentId?: string) => ["aiAgents", "sessions", agentId],
} as const;

export const useAgentsInfiniteQuery = (params: {
  search: Ref<string>;
  [key: string]: unknown;
}) => {
  return useInfiniteQuery<
    IPaginatedResponse<AiAgentBase>,
    Error,
    InfiniteData<IPaginatedResponse<AiAgentBase>>,
    (string | number)[],
    number
  >({
    queryKey: QUERY_KEYS.agents(params.search),
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = {
        ...params,
        search: params.search.value,
        page: pageParam,
      };
      return await aiAgentsApiService.getAll(queryParams);
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = lastPage.meta;
      return current_page < last_page ? current_page + 1 : undefined;
    },
    initialPageParam: 1,
    gcTime: 0,
    staleTime: 0,
  });
};

export const useAgentSessionsQuery = (params?: {
  agentId?: MaybeRef<string | undefined>;
}) => {
  const { agentSessionInfo } = storeToRefs(useAiAgentsStore());
  const agentId = computed(() => unref(params?.agentId));

  return useQuery<AiAgentSession[]>({
    queryKey: computed(() => QUERY_KEYS.agentSessions(agentId.value)),
    queryFn: async () => {
      let data;
      if (agentId.value) {
        data = await aiAgentsApiService.getAllAgentSessions({
          agent_id: agentId.value,
        });
      } else {
        data = await aiAgentsApiService.getAllAgentsSessions();
      }
      agentSessionInfo.value.previous_sessions = data;
      return data;
    },
  });
};

export const useAgentVisibilityMutation = () => {
  const { t } = useI18n();
  const { showMessage } = useNotification();

  return useMutation({
    mutationFn: async ({
      payload,
      role,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      search,
    }: {
      payload: UpdateAgentsVisibilityPayload;
      role: AgentVisibilityType;
      search: Ref<string>;
    }): Promise<void> => {
      await aiAgentsApiService.updateAgentsVisibility({
        role,
        payload,
      });
    },
    onMutate: async (updatedVisibility) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: QUERY_KEYS.agents(updatedVisibility.search),
      });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(
        QUERY_KEYS.agents(updatedVisibility.search)
      );

      // Optimistically update to the new value
      queryClient.setQueryData(
        QUERY_KEYS.agents(updatedVisibility.search),
        (prevSettings: InfiniteData<IPaginatedResponse<AiAgentBase>>) => ({
          ...prevSettings,
          pages:
            prevSettings?.pages?.map((page) => ({
              ...page,
              data: page.data.map((agent) => {
                const updatedVisibilityType = updatedVisibility.role;
                const { id, is_visible } =
                  updatedVisibility.payload.ai_agents[0];

                if (agent.id !== id) {
                  return agent;
                }

                return {
                  ...agent,
                  visibilities: getUpdatedVisibilities(
                    agent.visibilities ?? [],
                    {
                      agent_id: id,
                      is_visible,
                      applies_to_type: updatedVisibilityType,
                      feature: AgentFeatureType.CommandBarAgentSearch,
                    }
                  ),
                };
              }),
            })) ?? [],
        })
      );

      return { previousSettings };
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.agents(variables.search),
      });
    },
    onError: (err, updatedSettings, context) => {
      queryClient.setQueryData(
        QUERY_KEYS.agents(updatedSettings.search),
        context?.previousSettings
      );
      showMessage(t("ERRORS.AGENT_VISIBILITY_UPDATE_ERROR"), "error");
    },
  });
};
